"use client";

import { c as _c } from "react/compiler-runtime";
import { useDesktopAnimations } from "@/lib/breakpoints";
import React from "react";
import Image from "next/image";
import GradientButton from "@/components/ui/GradientButton";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
export interface LaravelFeatureCard {
  icon: string;
  title: string;
  alt?: string;
}
export interface LaravelBadge {
  icon: string;
  fullText: string;
  line1: string;
  line2: string;
}
interface LaravelDeveloperHeroProps {
  title?: React.ReactNode;
  description?: string;
  buttonText?: string;
  buttonHref?: string;
  imageSrc?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  variant?: "default" | "cards-bottom";
  badges?: LaravelBadge[];
  featureCards?: LaravelFeatureCard[];
}
const defaultBadges: LaravelBadge[] = [{
  icon: "/images/laravel/project-completed.png",
  fullText: "150+ Project Completed",
  line1: "150+ Project",
  line2: "Completed"
}, {
  icon: "/images/laravel/agile-methodology.png",
  fullText: "Follow Agile Methodology",
  line1: "Follow Agile",
  line2: "Methodology"
}, {
  icon: "/images/laravel/project-delivery.png",
  fullText: "On-Time Delivery",
  line1: "On-Time",
  line2: "Delivery"
}];
const defaultFeatureCards: LaravelFeatureCard[] = [{
  icon: "/images/laravel/project-deliverys.png",
  title: "90% On-time Laravel Project Delivery",
  alt: "On-time delivery"
}, {
  icon: "/images/laravel/infrastructure-cost.png",
  title: "2X Faster & 40% Cost Savings",
  alt: "Cost savings"
}, {
  icon: "/images/crm/key-benefit-5.png",
  title: "Choose from 500+ Web App Developers",
  alt: "Web app developers"
}, {
  icon: "/images/laravel/security-Reliability.png",
  title: "Scalable, Secure & Reliable Team",
  alt: "Scalable and secure"
}];
export default function LaravelDeveloperHero(t0) {
  const $ = _c(86);
  const {
    title: t1,
    description: t2,
    buttonText: t3,
    buttonHref: t4,
    imageSrc: t5,
    imageAlt: t6,
    imageWidth: t7,
    imageHeight: t8,
    variant: t9,
    badges: t10,
    featureCards: t11
  } = t0;
  const title = t1 === undefined ? "Transform Ideas into Reality with Our Laravel Experts" : t1;
  const description = t2 === undefined ? "Join 36%+ businesses using Laravel\u2014hire expert developers from The Dezine in 24 hours. Get top performance and seamless functionality for your project." : t2;
  const buttonText = t3 === undefined ? "Hire Us Now!" : t3;
  const buttonHref = t4 === undefined ? "/career" : t4;
  const imageSrc = t5 === undefined ? "/images/laravel/hire-laravel-developers.png" : t5;
  const imageAlt = t6 === undefined ? "Laravel expert developers building high-performance web applications" : t6;
  const imageWidth = t7 === undefined ? 622 : t7;
  const imageHeight = t8 === undefined ? 490 : t8;
  const variant = t9 === undefined ? "default" : t9;
  const badges = t10 === undefined ? defaultBadges : t10;
  const featureCards = t11 === undefined ? defaultFeatureCards : t11;
  const desktopAnimations = useDesktopAnimations();
  const reduceMotion = useReducedMotion();
  const t12 = reduceMotion ? 0 : 34;
  let t13;
  if ($[0] !== t12) {
    t13 = {
      opacity: 0,
      y: t12
    };
    $[0] = t12;
    $[1] = t13;
  } else {
    t13 = $[1];
  }
  let t14;
  if ($[2] !== reduceMotion) {
    t14 = delay => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: reduceMotion ? 0 : 0.7,
        delay,
        ease: "easeOut" as const
      }
    });
    $[2] = reduceMotion;
    $[3] = t14;
  } else {
    t14 = $[3];
  }
  let t15;
  if ($[4] !== t13 || $[5] !== t14) {
    t15 = {
      hidden: t13,
      visible: t14
    };
    $[4] = t13;
    $[5] = t14;
    $[6] = t15;
  } else {
    t15 = $[6];
  }
  const reveal = t15;
  const isCardsBottom = variant === "cards-bottom";
  const t16 = `relative isolate overflow-hidden text-[#050748] ${isCardsBottom ? "bg-[linear-gradient(180deg,#fff8fb_0%,#d0fcf6_100%)] dark:bg-[linear-gradient(180deg,#0b0f19_0%,#091a1e_100%)]" : "bg-[linear-gradient(180deg,#ffffff_0%,#f8ffff_52%,#d9fffa_100%)] dark:bg-[linear-gradient(180deg,#0b0f19_0%,#0e1526_52%,#091a1e_100%)]"}`;
  let t17;
  let t18;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="absolute inset-0 -z-20 opacity-50 bg-[radial-gradient(circle,rgba(75,88,125,.18)_0.8px,transparent_0.9px)] bg-[length:7px_7px] [mask-image:linear-gradient(to_bottom,#000_0%,rgba(0,0,0,.7)_82%,transparent_100%)]" aria-hidden="true" />;
    t18 = <div className="absolute -right-[10%] -bottom-[35%] -z-10 h-[80%] w-[75%] rounded-full bg-[rgba(91,244,220,.15)] blur-[70px]" aria-hidden="true" />;
    $[7] = t17;
    $[8] = t18;
  } else {
    t17 = $[7];
    t18 = $[8];
  }
  const t19 = isCardsBottom ? "relative mx-auto w-full max-w-[1200px] px-5 pb-16 pt-24 sm:px-8 lg:px-[15px] lg:pb-[100px] lg:pt-[100px]" : "relative mx-auto w-[min(100%-40px,1280px)] min-h-[560px] pt-[clamp(110px,12vh,140px)] pb-16 sm:pb-20 lg:pb-24 max-[1100px]:pt-[105px] max-[820px]:min-h-0 max-[820px]:w-[min(100%-36px,680px)] max-[820px]:pt-[105px] max-[820px]:pb-16 max-[520px]:w-[min(100%-28px,460px)] max-[520px]:pt-[95px]";
  const t20 = isCardsBottom ? "flex flex-col items-center gap-10 lg:flex-row lg:items-center lg:gap-[30px]" : "flex flex-col lg:flex-row items-center lg:items-start justify-between gap-10 lg:gap-12";
  const t21 = isCardsBottom ? "relative z-10 w-full text-left lg:w-1/2" : "relative z-10 w-full lg:w-[58%] pt-[3px] text-left";
  const t22 = isCardsBottom ? "m-0 text-left font-poppins text-[30px] font-semibold leading-[40px] text-[#050748] sm:text-[34px] sm:leading-[46px] lg:text-[40px] lg:leading-[52px] dark:text-white" : "m-0 max-w-[680px] text-left font-poppins text-[clamp(36px,3.3vw,52px)] font-bold leading-[1.15] tracking-[-0.03em] text-[#050748] max-[1100px]:text-[clamp(32px,4vw,46px)] max-[520px]:text-[clamp(28px,9vw,38px)] dark:text-white";
  let t23;
  if ($[9] !== t22 || $[10] !== title) {
    t23 = <h1 id="laravel-hero-title" className={t22}>{title}</h1>;
    $[9] = t22;
    $[10] = title;
    $[11] = t23;
  } else {
    t23 = $[11];
  }
  const t24 = isCardsBottom ? "mb-[35px] pt-[30px] text-left font-open-sans text-[17px] font-normal leading-[28px] text-[#6a6a8e] sm:pt-[40px] lg:text-justify lg:text-[20px] lg:leading-[30px] dark:text-gray-300" : "mb-[32px] mt-[28px] max-w-[640px] text-left font-sans text-[clamp(15.5px,1.2vw,18px)] font-normal leading-[1.6] text-[#66698a] max-[1100px]:mt-[22px] max-[820px]:mt-[20px] max-[820px]:mb-[26px] dark:text-gray-300";
  const t25 = desktopAnimations ? "hidden" : false;
  let t26;
  if ($[12] !== description || $[13] !== reveal || $[14] !== t24 || $[15] !== t25) {
    t26 = <motion.p className={t24} variants={reveal} initial={t25} animate="visible" custom={0.2}>{description}</motion.p>;
    $[12] = description;
    $[13] = reveal;
    $[14] = t24;
    $[15] = t25;
    $[16] = t26;
  } else {
    t26 = $[16];
  }
  let t27;
  if ($[17] !== badges || $[18] !== desktopAnimations || $[19] !== isCardsBottom || $[20] !== reveal) {
    t27 = !isCardsBottom && <motion.div variants={reveal} initial={desktopAnimations ? "hidden" : false} animate="visible" custom={0.28} className="mb-8 flex w-full max-w-[650px] flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-3.5">{badges.map(_temp)}</motion.div>;
    $[17] = badges;
    $[18] = desktopAnimations;
    $[19] = isCardsBottom;
    $[20] = reveal;
    $[21] = t27;
  } else {
    t27 = $[21];
  }
  const t28 = desktopAnimations ? "hidden" : false;
  const t29 = isCardsBottom ? "group inline-flex h-14 items-center justify-center gap-2 rounded-full px-8 font-poppins text-base font-normal text-white no-underline transition-all duration-200 ease-out  focus-visible:outline focus-visible:outline-3 focus-visible:outline-[rgba(5,7,72,.38)] focus-visible:outline-offset-4" : "group inline-flex min-h-[56px] min-w-[175px] items-center justify-center gap-[6px] rounded-full px-[26px] pl-[30px] py-[13px] font-poppins text-[16.5px] font-semibold text-white no-underline  transition-all duration-200 ease-out focus-visible:outline focus-visible:outline-3 focus-visible:outline-[rgba(5,7,72,.38)] focus-visible:outline-offset-4 max-[520px]:min-h-[50px] max-[520px]:min-w-[155px] max-[520px]:text-[15.5px]";
  let t30;
  if ($[22] !== buttonText) {
    t30 = <span>{buttonText}</span>;
    $[22] = buttonText;
    $[23] = t30;
  } else {
    t30 = $[23];
  }
  let t31;
  if ($[24] !== isCardsBottom) {
    t31 = isCardsBottom ? <ChevronRight className="transition-transform duration-200 ease-out group-hover:translate-x-[4px] motion-reduce:transition-none" aria-hidden="true" size={19} strokeWidth={2.5} /> : <ArrowRight className="transition-transform duration-200 ease-out group-hover:translate-x-[4px] motion-reduce:transition-none" aria-hidden="true" size={19} strokeWidth={2.2} />;
    $[24] = isCardsBottom;
    $[25] = t31;
  } else {
    t31 = $[25];
  }
  let t32;
  if ($[26] !== buttonHref || $[27] !== t29 || $[28] !== t30 || $[29] !== t31) {
    t32 = <GradientButton variant="pink-purple" className={t29} href={buttonHref}>{t30}{t31}</GradientButton>;
    $[26] = buttonHref;
    $[27] = t29;
    $[28] = t30;
    $[29] = t31;
    $[30] = t32;
  } else {
    t32 = $[30];
  }
  let t33;
  if ($[31] !== reveal || $[32] !== t28 || $[33] !== t32) {
    t33 = <motion.div variants={reveal} initial={t28} animate="visible" custom={0.32} className="flex justify-center sm:justify-start">{t32}</motion.div>;
    $[31] = reveal;
    $[32] = t28;
    $[33] = t32;
    $[34] = t33;
  } else {
    t33 = $[34];
  }
  let t34;
  if ($[35] !== t21 || $[36] !== t23 || $[37] !== t26 || $[38] !== t27 || $[39] !== t33) {
    t34 = <div className={t21}>{t23}{t26}{t27}{t33}</div>;
    $[35] = t21;
    $[36] = t23;
    $[37] = t26;
    $[38] = t27;
    $[39] = t33;
    $[40] = t34;
  } else {
    t34 = $[40];
  }
  const t35 = isCardsBottom ? "relative z-10 flex w-full items-center justify-center lg:w-1/2" : "relative z-10 w-full lg:w-[42%] flex justify-center items-center";
  let t36;
  if ($[41] !== desktopAnimations || $[42] !== reduceMotion) {
    t36 = desktopAnimations ? {
      opacity: 0,
      x: reduceMotion ? 0 : 50,
      scale: reduceMotion ? 1 : 0.96
    } : false;
    $[41] = desktopAnimations;
    $[42] = reduceMotion;
    $[43] = t36;
  } else {
    t36 = $[43];
  }
  let t37;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      opacity: 1,
      x: 0,
      scale: 1
    };
    $[44] = t37;
  } else {
    t37 = $[44];
  }
  const t38 = reduceMotion ? 0 : 0.8;
  let t39;
  if ($[45] !== t38) {
    t39 = {
      duration: t38,
      delay: 0.16,
      ease: "easeOut"
    };
    $[45] = t38;
    $[46] = t39;
  } else {
    t39 = $[46];
  }
  const t40 = isCardsBottom ? "block h-auto w-full max-w-[570px]" : "block h-auto max-h-[460px] sm:max-h-[520px] w-full";
  let t41;
  if ($[47] !== desktopAnimations || $[48] !== isCardsBottom) {
    t41 = desktopAnimations ? isCardsBottom ? {
      y: [0, 10, 0]
    } : {
      y: [0, -14, 0]
    } : undefined;
    $[47] = desktopAnimations;
    $[48] = isCardsBottom;
    $[49] = t41;
  } else {
    t41 = $[49];
  }
  let t42;
  if ($[50] !== desktopAnimations || $[51] !== isCardsBottom) {
    t42 = desktopAnimations ? {
      duration: isCardsBottom ? 3 : 4.5,
      repeat: Infinity,
      ease: "easeInOut"
    } : undefined;
    $[50] = desktopAnimations;
    $[51] = isCardsBottom;
    $[52] = t42;
  } else {
    t42 = $[52];
  }
  let t43;
  if ($[53] !== imageAlt || $[54] !== imageHeight || $[55] !== imageSrc || $[56] !== imageWidth) {
    t43 = <Image src={imageSrc} alt={imageAlt} width={imageWidth} height={imageHeight} priority={true} className="h-auto w-full object-contain" />;
    $[53] = imageAlt;
    $[54] = imageHeight;
    $[55] = imageSrc;
    $[56] = imageWidth;
    $[57] = t43;
  } else {
    t43 = $[57];
  }
  let t44;
  if ($[58] !== t40 || $[59] !== t41 || $[60] !== t42 || $[61] !== t43) {
    t44 = <motion.div className={t40} animate={t41} transition={t42}>{t43}</motion.div>;
    $[58] = t40;
    $[59] = t41;
    $[60] = t42;
    $[61] = t43;
    $[62] = t44;
  } else {
    t44 = $[62];
  }
  let t45;
  if ($[63] !== t35 || $[64] !== t36 || $[65] !== t39 || $[66] !== t44) {
    t45 = <motion.div className={t35} initial={t36} animate={t37} transition={t39}>{t44}</motion.div>;
    $[63] = t35;
    $[64] = t36;
    $[65] = t39;
    $[66] = t44;
    $[67] = t45;
  } else {
    t45 = $[67];
  }
  let t46;
  if ($[68] !== t20 || $[69] !== t34 || $[70] !== t45) {
    t46 = <div className={t20}>{t34}{t45}</div>;
    $[68] = t20;
    $[69] = t34;
    $[70] = t45;
    $[71] = t46;
  } else {
    t46 = $[71];
  }
  let t47;
  if ($[72] !== t19 || $[73] !== t46) {
    t47 = <div id="hero-inner" className={t19}>{t46}</div>;
    $[72] = t19;
    $[73] = t46;
    $[74] = t47;
  } else {
    t47 = $[74];
  }
  let t48;
  if ($[75] !== t16 || $[76] !== t47) {
    t48 = <section id="laravel-hero-section" className={t16} aria-labelledby="laravel-hero-title">{t17}{t18}{t47}</section>;
    $[75] = t16;
    $[76] = t47;
    $[77] = t48;
  } else {
    t48 = $[77];
  }
  let t49;
  if ($[78] !== desktopAnimations || $[79] !== featureCards || $[80] !== isCardsBottom || $[81] !== reveal) {
    t49 = isCardsBottom && featureCards && featureCards.length > 0 && <motion.div variants={reveal} initial={desktopAnimations ? "hidden" : false} animate="visible" custom={0.42} className="relative z-10 mx-auto -mt-10 w-full max-w-[1200px] px-5 pb-14 sm:px-8 lg:-mt-[76px] lg:px-[15px] lg:pb-[100px]"><div className="mx-auto grid max-w-[950px] grid-cols-1 gap-[30px] sm:grid-cols-2 lg:grid-cols-4">{featureCards.map(_temp3)}</div></motion.div>;
    $[78] = desktopAnimations;
    $[79] = featureCards;
    $[80] = isCardsBottom;
    $[81] = reveal;
    $[82] = t49;
  } else {
    t49 = $[82];
  }
  let t50;
  if ($[83] !== t48 || $[84] !== t49) {
    t50 = <>{t48}{t49}</>;
    $[83] = t48;
    $[84] = t49;
    $[85] = t50;
  } else {
    t50 = $[85];
  }
  return t50;
}
function _temp3(card, idx_0) {
  return <div key={idx_0} className="group relative flex h-full flex-col items-center justify-start bg-white p-6 text-center shadow-[0_8px_16px_rgba(0,0,0,0.15)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_14px_24px_rgba(0,0,0,0.18)] dark:bg-gray-900/90"><div className="mb-4 w-1/2"><Image src={card.icon} alt={card.alt || card.title} width={84} height={84} loading="lazy" className="h-auto w-full object-contain transition-transform duration-300 group-hover:scale-105" onError={_temp2} /></div><p className="m-0 font-open-sans text-[15px] font-normal leading-[26px] text-[#6a6a8e] sm:text-[17px] sm:leading-[28px] dark:text-gray-300">{card.title}</p></div>;
}
function _temp2(e) {
  e.currentTarget.src = "/images/laravel/project-delivery.png";
}
function _temp(badge, idx) {
  return <div key={idx} className="flex w-full sm:w-auto flex-col sm:flex-row items-center justify-center sm:justify-start text-center sm:text-left rounded-[14px] sm:rounded-[12px] border border-gray-100 bg-white/95 px-4 py-4 sm:px-4 sm:py-3.5 shadow-[0_4px_18px_rgba(11,19,84,0.06)] backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80"><div className="mb-2 sm:mb-0 sm:mr-3 flex h-9 w-9 shrink-0 items-center justify-center"><Image src={badge.icon} alt={badge.fullText} width={36} height={36} loading="lazy" className="h-full w-full object-contain" /></div><span className="block sm:hidden font-sans text-[14.5px] font-bold text-[#0b1354] dark:text-white">{badge.fullText}</span><div className="hidden sm:flex flex-col text-left leading-[1.25]"><span className="whitespace-nowrap font-sans text-[13.5px] font-bold text-[#0b1354] dark:text-white">{badge.line1}</span><span className="whitespace-nowrap font-sans text-[13.5px] font-bold text-[#0b1354] dark:text-white">{badge.line2}</span></div></div>;
}